import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { isNullOrUndefined } from 'utils/object-utils';
import { useParams, useNavigate } from 'react-router-dom';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import SlideToggle from 'components/common/slide-toggle-component';
import InfoIconComponent from './side-by-side-info-icon';
import CreateWorkpaperModal from 'components/feature/element-panel/workpaper-panel/create-work-paper-modal';
import Dropdown from 'pages/side-by-side-compare/dropdown/side-by-side-dropdown';
import { ReactComponent as BackButton } from 'icons/back-button.svg';
import { ReactComponent as Plus } from 'icons/plus.svg';
import RolesUtil from 'permissions/utils/roles';
import AnnotationDisplayOptions from 'models/data/annotation-display-filter-options-model';
import { ROUTE_CONSTANTS, parseRoute } from 'constants/util/route-constants';
import { createOrUpdateElementWorkpaperRequestAutoTieout } from 'api/workpaper-api';
import { unlinkOmniaSnapshotStatement } from 'api/statement-api';
import ToolTip from 'components/common/tool-tip-component';
import { useDispatch, useSelector } from 'react-redux';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  SIDE_BY_SIDE_COMPARE_MODE,
  AUTO_TIEOUT_COMPARE_MODE_NEW,
  AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
  AUTO_TIEOUT_COMPARE_MODE_SUCCESS,
  AUTO_TIEOUT_COMPARE_MODE_FAILED,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import {
  dropdownOptionsForRevision,
  dropdownOptionsForStatements,
  TAB_NAME,
} from 'constants/util/side-by-side-utils';
import SetThresholdScalingModal from 'components/feature/trial-balance/set-threshold-scaling-modal';
import DisplayThresholdAndScaling from 'components/feature/trial-balance/display-threshold-scaling';
import TrialBalanceUnlink from './side-by-side-view-trial-balance-unlink';
import { StatusCodes } from 'http-status-codes';
import {
  resetTrialBalanceView,
  setHighlightMappedElements,
  performTBAutoTieoutOnUpdatedSnapshot,
} from 'store/actions/trial-balance/trial-balance-action';
import DisplayElementsMappedCount from './side-by-side-view-element-mapped-count';
import StatementSummaryElements from 'models/api/statement-summary-elements-model';
import StatementSummary from 'components/feature/statement-summary/statement-summary-component';
import { ReactComponent as Edit } from 'icons/edit.svg';

const AUTO_TIEOUT = 'AUTO_TIEOUT';
const AUTO_TIEOUT_TB = 'AUTO_TIEOUT_TB';
const ATTACH_WORKPAPER_ID_BLOCK = 'attach-work-paper-id';
const EDIT_ICON_SIZE = 20;

const Header = ({
  BLOCK,
  selectedTab,
  setAnnotationsForMapping,
  sideBySideSyncScroll,
  setSideBySideSyncScroll,
  shouldDisableSyncScroll,
  sideBySideViewMode,
  leftCache,
  initiateAutoTieout,
  currentUserId,
  clearAllLeftStatementData,
  setAutoTieoutWorkpaperReference,
  workpaperDetails,
  resetAutoTieoutStatusForBanner,
  stateData,
  removeAutoTieoutWorkpaperReference,
  setShowAutoTieoutInfoBanner,
  isSnapshotViewLoaded,
  thresholdScaling,
  selectedStatement,
  taggedElements,
  syncScrollIds,
  isSnapshotReadOnly,
  workFlowStatus,
  socketHasBeenDisconnected,
  highlightMappedElements,
  mappedElements,
  scalingOption,
  setScalingOption,
  thresholdOption,
  setThresholdOption,
  editPermissionAutotieout,
}) => {
  const { projectId, readOnly, revisionId, statementId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tbWorkflowStatus = useSelector(
    (state) => state?.ui.sideBySideView?.trialBalance?.tbWorkFlowStatus,
  );
  const [showCreateWorkpaperModal, setShowCreateWorkpaperModal] =
    useState(false);
  const [showThresholdAndScalingModal, setShowThresholdAndScalingModal] =
    useState(false);
  const isTargetStatementWrappedUpOrPendingApproval =
    selectedStatement?.isStatementWrappedOrPendingApproval();

  const isSideBySideMode =
    sideBySideViewMode?.viewMode === SIDE_BY_SIDE_COMPARE_MODE;
  const isAutoTieoutMode =
    sideBySideViewMode?.viewMode === AUTO_TIEOUT_COMPARE_MODE;
  const isTrialBalanceMode =
    sideBySideViewMode?.viewMode === TRAIL_BALANCE_MODE;
  // disabling create WPR button if the target statement is wrapped up or pending approval or User has Observer role or snapshot is in readonly mode
  const isCreateButtonDisabled =
    isTargetStatementWrappedUpOrPendingApproval ||
    selectedStatement?.data?.readOnlyOverride || //disable if previous revision of current statement is opened/readOnly Statement is opened in side by side view.
    // RolesUtil.doesUserHaveObserverRoleForProject(stateData, projectId) ||
    !editPermissionAutotieout ||
    (isTrialBalanceMode && isSnapshotReadOnly);

  const getAutoTieoutDisable = () => {
    // disabling auto tieout button if the target statement is wrapped up or pending approval or User has Observer role or snapshot is in readonly mode
    if (isCreateButtonDisabled) return true;

    // initially disabling auto tieout button if workpaper reference number is not present
    if (
      !workpaperDetails?.referenceNumber ||
      !editPermissionAutotieout ||
      (isTrialBalanceMode &&
        isSnapshotViewLoaded &&
        !taggedElements.hasTaggedElements)
    ) {
      return true;
    }

    const autoTieoutStatus = sideBySideViewMode.getAutoTieoutStatusForBanner();

    // If auto tieout status is null/undefined and we have workpaper reference, enable the button to allow initiating auto tieout
    if (isNullOrUndefined(autoTieoutStatus)) {
      return false;
    }
    if (autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_FAILED) {
      return true;
    }

    // if auto tieout status is success enable the auto tieout button to revalidate auto tieout
    if (autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_SUCCESS) {
      return false;
    }

    // Disabling auto tieout button if the workflow is in progress or newly created
    return (
      autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_INPROGRESS ||
      autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_NEW
    );
  };

  const isAutoTieoutDisabled = getAutoTieoutDisable();

  const onBackButtonClick = () => {
    navigate(
      parseRoute(ROUTE_CONSTANTS.STATEMENT_CONTENT_PAGE, {
        params: {
          projectId,
          statementId,
          revisionId,
          readOnly,
        },
      }),
    );
    clearAllLeftStatementData();

    if (isAutoTieoutMode || isTrialBalanceMode) {
      resetAutoTieoutStatusForBanner();
      removeAutoTieoutWorkpaperReference();
      setShowAutoTieoutInfoBanner(false);
    }
  };

  const toggleCreateWorkPaperModal = () => {
    setShowCreateWorkpaperModal(!showCreateWorkpaperModal);
  };

  const toggleSetThresholdAndScalingModal = () => {
    setShowThresholdAndScalingModal(!showThresholdAndScalingModal);
  };
  const isUnlinkDisabled =
    selectedStatement.isInWrappedUpFlow() ||
    selectedStatement?.data?.readOnlyOverride || //disable if previous revision of current statement is opened/readOnly Statement is opened in side by side view.
    !editPermissionAutotieout;

  const handleCreateNewWorkpaper = async ({ workpaperModel }) => {
    const workpaperData = await createOrUpdateElementWorkpaperRequestAutoTieout(
      {
        workpaperModel,
        clientId: projectId.split('-')[0],
      },
    );
    setAutoTieoutWorkpaperReference(workpaperData);
    toggleCreateWorkPaperModal();
  };

  const handleAutoTieoutProcess = async ({
    threshold,
    scaling,
    withoutModal = false,
  } = {}) => {
    const thresholdScalingValues = {
      threshold: withoutModal
        ? Number(thresholdScaling?.threshold)
        : Number(threshold),
      scaling: withoutModal ? thresholdScaling?.scaling : scaling,
    };
    try {
      if (isTrialBalanceMode && tbWorkflowStatus?.stale) {
        setShowAutoTieoutInfoBanner(true);
        dispatch(
          performTBAutoTieoutOnUpdatedSnapshot({
            clientId: parseInt(projectId),
            statementId,
            revisionId,
          }),
        );
      } else {
        initiateAutoTieout(thresholdScalingValues);
        if (!withoutModal) {
          toggleSetThresholdAndScalingModal();
        }
      }
    } catch (error) {
      console.error(
        'Error in initiating Trail balance Auto-tieout process:',
        error,
      );
    }
  };

  const unlinkOmniaSnapshot = async () => {
    try {
      return await unlinkOmniaSnapshotStatement({
        clientId: parseInt(projectId),
        statementId,
        revisionId,
      });
    } catch (error) {
      console.error('Error unlinking trial balance data:', error);
      return null;
    }
  };

  const onClickUnlinkOmniaSnapshot = async () => {
    const response = await unlinkOmniaSnapshot();
    if (response?.status === StatusCodes.OK && socketHasBeenDisconnected) {
      dispatch(resetTrialBalanceView({ syncScrollIds }));
    }
  };

  const getHeaderTitle = () => {
    if (isSideBySideMode) {
      return 'side-by-side-view.compare.radio-button.side-by-side-option';
    }
    if (isTrialBalanceMode) {
      return 'side-by-side-view.compare.radio-button.auto-tieout-trial-balance-option';
    }
    if (sideBySideViewMode.viewMode === '') {
      return 'toolkit.icon.sidebyside.tooltip';
    }
    return 'side-by-side-view.compare.radio-button.auto-tieout-option';
  };

  return (
    <div className={`${BLOCK}__header`}>
      <div className={`${BLOCK}__header-container`}>
        <Button.IconButton
          id={`${BLOCK}-id__header-back-button`}
          className={`${BLOCK}__header-back-button`}
          type={BUTTON_TYPES.icon}
          Icon={BackButton}
          onClick={onBackButtonClick}
        />
        <div className={`${BLOCK}__header-title`}>
          <FormattedMessage id={getHeaderTitle()} />
        </div>
        {sideBySideViewMode.viewMode === '' && (
          <InfoIconComponent
            id={`${BLOCK}-id-info-tooltip`}
            tooltip={'side-by-side-view.compare.header.tooltip.info'}
          />
        )}
      </div>
      <div className={`${BLOCK}__scroll-toggle-container`}>
        {!isTrialBalanceMode && (
          <>
            <div className={`${BLOCK}__scroll-title`}>
              <FormattedMessage id={'compare-panel.sync-scroll.toggle.label'} />
            </div>

            <div className={`${BLOCK}__scroll-toggle`}>
              <SlideToggle
                name={'compare-panel.sync-scroll.toggle.name'}
                onChange={() => {
                  setSideBySideSyncScroll(!sideBySideSyncScroll);
                }}
                disabled={shouldDisableSyncScroll}
                checked={sideBySideSyncScroll}
                id={'synchronizing-scroll-toggle'}
              />
            </div>
          </>
        )}

        <StatementSummary className={`${BLOCK}__summary`} />

        {((isAutoTieoutMode && leftCache.isLoaded) ||
          (isTrialBalanceMode && isSnapshotViewLoaded)) && (
          <>
            {workpaperDetails?.referenceNumber ? (
              <div className={`${BLOCK}__workpaper-reference`}>
                {workpaperDetails?.referenceNumber}
              </div>
            ) : (
              <div className={`${BLOCK}__workpaper-create`}>
                <Button.IconButton
                  id={`${ATTACH_WORKPAPER_ID_BLOCK}__add-link`}
                  onClick={() => toggleCreateWorkPaperModal()}
                  Icon={Plus}
                  iconSize={14}
                  disabled={isCreateButtonDisabled}
                >
                  <FormattedMessage id="common.create" />
                </Button.IconButton>
              </div>
            )}
            <div className={`${BLOCK}__auto-tieout-wrapper`}>
              <div className={`${BLOCK}__auto-tieout`}>
                <Button
                  id={'auto-tieout-button__add-link'}
                  type={BUTTON_TYPES.secondary}
                  onClick={() =>
                    handleAutoTieoutProcess({ withoutModal: true })
                  }
                  disabled={isAutoTieoutDisabled}
                >
                  <FormattedMessage
                    id={'side-by-side-view.compare.header.auto-tieout.button'}
                  />
                </Button>
              </div>
              {isTrialBalanceMode ? (
                <InfoIconComponent
                  id={`${BLOCK}-id-auto-tieout-btn-info-tooltip`}
                  tooltip="trial-balance-view.auto-tieout.info.tooltip"
                  defaultExpanded
                />
              ) : (
                <InfoIconComponent
                  id={`${BLOCK}-id-auto-tieout-btn-info-tooltip`}
                  tooltip="side-by-side-view.compare.header.auto-tieout.button.tooltip.message"
                  defaultExpanded
                />
              )}
            </div>
            {(thresholdScaling?.threshold || thresholdScaling?.scaling) && (
              <DisplayThresholdAndScaling
                BLOCK={BLOCK}
                thresholdScaling={thresholdScaling}
                displayScaling={isTrialBalanceMode}
              />
            )}
            <Edit
              id={`${BLOCK}-edit-project-button`}
              className={classNames(`${BLOCK}__edit-button`, {
                [`${BLOCK}__edit-button--disabled`]: isAutoTieoutDisabled,
              })}
              role={'button'}
              width={EDIT_ICON_SIZE}
              height={EDIT_ICON_SIZE}
              onClick={() => {
                if (!isAutoTieoutDisabled) toggleSetThresholdAndScalingModal();
              }}
            />
          </>
        )}
      </div>
      {showCreateWorkpaperModal && (
        <CreateWorkpaperModal
          onClose={toggleCreateWorkPaperModal}
          onCreate={handleCreateNewWorkpaper}
          autoTieoutWorkpaper={{
            revisionId,
            workpaperType: isTrialBalanceMode ? AUTO_TIEOUT_TB : AUTO_TIEOUT,
            currentUserId,
          }}
          isAutoTieout
        />
      )}
      {showThresholdAndScalingModal && (
        <SetThresholdScalingModal
          onClose={toggleSetThresholdAndScalingModal}
          onCreate={handleAutoTieoutProcess}
          displayScaling={isTrialBalanceMode}
          setShowAutoTieoutInfoBanner={setShowAutoTieoutInfoBanner}
          params={{ clientId: parseInt(projectId), statementId, revisionId }}
          scalingOption={scalingOption}
          setScalingOption={setScalingOption}
          thresholdOption={thresholdOption}
          setThresholdOption={setThresholdOption}
          thresholdScaling={thresholdScaling}
          title={
            isTrialBalanceMode
              ? 'trial-balance-view-set-threshold-and-scaling.modal.title'
              : 'trial-balance-view-set-threshold.modal.title'
          }
        />
      )}
      {((isTrialBalanceMode && isSnapshotViewLoaded) ||
        (isAutoTieoutMode && leftCache.isLoaded)) && (
        <div className={`${BLOCK}__trial-balance-controls`}>
          {workFlowStatus?.isCompleted &&
            mappedElements?.getMappedElementsCount() > 0 && (
              <>
                <div className={`${BLOCK}__highlight-toggle`}>
                  <ToolTip
                    id={`${BLOCK}__toggle-tooltip`}
                    text={'auto-tieout-mapped-element-highlight-toggle.title'}
                    position={'top'}
                  >
                    <div>
                      <SlideToggle
                        name={
                          'auto-tieout.mapped.element.highlight.toggle.name'
                        }
                        onChange={() => {
                          dispatch(
                            setHighlightMappedElements(
                              !highlightMappedElements,
                            ),
                          );
                        }}
                        disabled={!workFlowStatus?.isCompleted}
                        checked={highlightMappedElements}
                        id={'highlight-mapped-element-toggle'}
                      />
                    </div>
                  </ToolTip>
                </div>
                <DisplayElementsMappedCount
                  BLOCK={BLOCK}
                  count={mappedElements?.getMappedElementsCount()}
                />
              </>
            )}
          {isTrialBalanceMode && isSnapshotViewLoaded && (
            <TrialBalanceUnlink
              BLOCK={BLOCK}
              isUnlinkDisabled={isUnlinkDisabled}
              onClickUnlinkOmniaSnapshot={onClickUnlinkOmniaSnapshot}
            />
          )}
        </div>
      )}
      {sideBySideViewMode.viewMode === SIDE_BY_SIDE_COMPARE_MODE && (
        <Dropdown
          options={
            selectedTab === TAB_NAME.LEFT
              ? dropdownOptionsForRevision
              : dropdownOptionsForStatements
          }
          setAnnotationsForMapping={setAnnotationsForMapping}
        />
      )}
    </div>
  );
};

Header.propTypes = {
  /** BEM block name for CSS class naming */
  BLOCK: PropTypes.string.isRequired,
  /** Currently selected tab (LEFT or RIGHT) */
  selectedTab: PropTypes.string,
  /** Function to set annotations for mapping */
  setAnnotationsForMapping: PropTypes.func.isRequired,
  /** Boolean flag indicating if side-by-side scroll sync is enabled */
  sideBySideSyncScroll: PropTypes.bool.isRequired,
  /** Function to toggle side-by-side scroll sync */
  setSideBySideSyncScroll: PropTypes.func.isRequired,
  /** Flag to disable sync scroll functionality */
  shouldDisableSyncScroll: PropTypes.bool.isRequired,
  /** Display options for annotations (showAll, etc.) */
  displayOptions: PropTypes.instanceOf(AnnotationDisplayOptions).isRequired,
  /** Function to update annotation display options */
  setAnnotationDisplayOption: PropTypes.func.isRequired,
  /** Function to enable/disable all element callouts */
  disableAllElementsCallout: PropTypes.func.isRequired,
  /** Function to set annotation position (left/right) */
  setAnnotationsPosition: PropTypes.func.isRequired,
  /** Current annotation position configuration */
  annotationPosition: PropTypes.shape({
    annotationPosition: PropTypes.string,
  }).isRequired,
  /** Function to set annotation font size */
  setAnnotationsFontSize: PropTypes.func.isRequired,
  /** Current view mode (SIDE_BY_SIDE, AUTO_TIEOUT, TRAIL_BALANCE) */
  sideBySideViewMode: PropTypes.shape({
    viewMode: PropTypes.string,
    getAutoTieoutStatusForBanner: PropTypes.func,
  }).isRequired,
  /** Left panel cache status */
  leftCache: PropTypes.shape({
    isLoaded: PropTypes.bool,
  }).isRequired,
  /** Function to initiate auto-tieout process */
  initiateAutoTieout: PropTypes.func.isRequired,
  /** Current user ID */
  currentUserId: PropTypes.string.isRequired,
  /** Function to clear all left statement data */
  clearAllLeftStatementData: PropTypes.func.isRequired,
  /** Function to set workpaper reference for auto-tieout */
  setAutoTieoutWorkpaperReference: PropTypes.func.isRequired,
  /** Workpaper details including reference number */
  workpaperDetails: PropTypes.shape({
    referenceNumber: PropTypes.string,
  }),
  /** Function to reset auto-tieout status for banner */
  resetAutoTieoutStatusForBanner: PropTypes.func.isRequired,
  /** Redux state data for permission checks */
  stateData: PropTypes.object.isRequired,
  /** Function to remove auto-tieout workpaper reference */
  removeAutoTieoutWorkpaperReference: PropTypes.func.isRequired,
  /** Function to show/hide auto-tieout info banner */
  setShowAutoTieoutInfoBanner: PropTypes.func.isRequired,
  /** Flag indicating if trial balance snapshot view is loaded */
  isSnapshotViewLoaded: PropTypes.bool.isRequired,
  /** Threshold and scaling values for auto-tieout */
  thresholdScaling: PropTypes.shape({
    threshold: PropTypes.number,
    scaling: PropTypes.number,
  }),
  /** Selected statement model with wrapped up flow check */
  selectedStatement: PropTypes.shape({
    isInWrappedUpFlow: PropTypes.func,
  }).isRequired,
  /** Sync scroll element IDs for scroll synchronization */
  syncScrollIds: PropTypes.shape({
    targetId: PropTypes.string,
  }).isRequired,
  /** Summary elements data for statement element tracking */
  summaryElements: PropTypes.instanceOf(StatementSummaryElements),
};

export default Header;
